"use client"

import { useState, useRef, useCallback } from "react"
import { aiService } from "../apis/aiService"

export const useGrammarCheck = () => {
  const [correctedTranscript, setCorrectedTranscript] = useState("")
  const [isGrammarChecking, setIsGrammarChecking] = useState(false)
  const [grammarSuggestions, setGrammarSuggestions] = useState([])
  const [showGrammarPanel, setShowGrammarPanel] = useState(false)

  const grammarCheckTimeoutRef = useRef(null)

  const applyBasicGrammarRules = useCallback((text) => {
    let corrected = text
    const grammarRules = [
      { pattern: /\b(i)\b/g, replacement: "I" },
      { pattern: /^\w/g, replacement: (match) => match.toUpperCase() },
      { pattern: /\.\s*\w/g, replacement: (match) => match.toUpperCase() },
      { pattern: /\bwanna\b/g, replacement: "want to" },
      { pattern: /\bgonna\b/g, replacement: "going to" },
      { pattern: /\bkinda\b/g, replacement: "kind of" },
      { pattern: /\bsorta\b/g, replacement: "sort of" },
      { pattern: /\byeah\b/g, replacement: "yes" },
      { pattern: /\bnah\b/g, replacement: "no" },
      { pattern: /\bum\b/g, replacement: "" },
      { pattern: /\buh\b/g, replacement: "" },
      { pattern: /\ber\b/g, replacement: "" },
      { pattern: /\ba\s+([aeiou])/g, replacement: "an $1" },
      { pattern: /\ban\s+([bcdfghjklmnpqrstvwxyz])/g, replacement: "a $1" },
      { pattern: /\s+/g, replacement: " " },
      { pattern: /\s+\./g, replacement: "." },
      { pattern: /\s+,/g, replacement: "," },
      { pattern: /\bteh\b/g, replacement: "the" },
      { pattern: /\badn\b/g, replacement: "and" },
    ]

    grammarRules.forEach((rule) => {
      corrected = corrected.replace(rule.pattern, rule.replacement)
    })
    return corrected.trim()
  }, [])

  const applyMedicalTerminologyCorrections = useCallback((text) => {
    const medicalCorrections = [
      { pattern: /\bdoctor\b/g, replacement: "physician" },
      { pattern: /\bmeds\b/g, replacement: "medications" },
      { pattern: /\bpills\b/g, replacement: "tablets" },
      { pattern: /\bshot\b/g, replacement: "injection" },
      { pattern: /\bsurg\b/g, replacement: "surgery" },
      { pattern: /\bop\b/g, replacement: "operation" },
      { pattern: /\bhurts\b/g, replacement: "causes pain" },
      { pattern: /\bfeels bad\b/g, replacement: "feels unwell" },
      { pattern: /\bfeels sick\b/g, replacement: "feels nauseous" },
      { pattern: /\bthrows up\b/g, replacement: "vomits" },
      { pattern: /\bcan't breathe\b/g, replacement: "experiences difficulty breathing" },
      { pattern: /\bstomach\b/g, replacement: "abdomen" },
      { pattern: /\bbelly\b/g, replacement: "abdomen" },
      { pattern: /\btummy\b/g, replacement: "abdomen" },
    ]

    let corrected = text
    medicalCorrections.forEach((correction) => {
      corrected = corrected.replace(correction.pattern, correction.replacement)
    })
    return corrected
  }, [])

  const improveSentenceStructure = useCallback((text) => {
    let sentences = text.split(/[.!?]+/).filter((s) => s.trim())
    sentences = sentences.map((sentence) => {
      sentence = sentence.trim()
      if (!sentence) return ""
      sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)
      if (!sentence.match(/[.!?]$/)) {
        sentence += "."
      }
      return sentence
    })
    return sentences.join(" ")
  }, [])

  const correctGrammar = useCallback(
    async (text) => {
      if (!text || text.trim().length < 10) return text
      try {
        const aiCorrected = await aiService.correctGrammar(text)
        let corrected = applyBasicGrammarRules(aiCorrected || text)
        corrected = applyMedicalTerminologyCorrections(corrected)
        corrected = improveSentenceStructure(corrected)
        return corrected
      } catch (error) {
        console.error("Grammar correction error:", error)
        return applyBasicGrammarRules(text)
      }
    },
    [applyBasicGrammarRules, applyMedicalTerminologyCorrections, improveSentenceStructure],
  )

  const generateGrammarSuggestions = useCallback((original, corrected) => {
    const suggestions = []
    const originalWords = original.split(/\s+/)
    const correctedWords = corrected.split(/\s+/)

    for (let i = 0; i < Math.max(originalWords.length, correctedWords.length); i++) {
      if (originalWords[i] !== correctedWords[i]) {
        suggestions.push({
          type: "grammar",
          original: originalWords[i] || "",
          suggestion: correctedWords[i] || "",
          description: "Grammar correction",
        })
      }
    }
    return suggestions.slice(0, 5)
  }, [])

  const checkGrammarWithDelay = useCallback(
    (text) => {
      if (grammarCheckTimeoutRef.current) {
        clearTimeout(grammarCheckTimeoutRef.current)
      }

      grammarCheckTimeoutRef.current = setTimeout(async () => {
        if (text.trim().length > 20) {
          setIsGrammarChecking(true)
          try {
            const corrected = await correctGrammar(text)
            setCorrectedTranscript(corrected)
            if (corrected !== text) {
              const suggestions = generateGrammarSuggestions(text, corrected)
              setGrammarSuggestions(suggestions)
              setShowGrammarPanel(true)
            }
          } catch (error) {
            console.error("Grammar check error:", error)
          }
          setIsGrammarChecking(false)
        }
      }, 1500)
    },
    [correctGrammar, generateGrammarSuggestions],
  )

  const applySuggestion = useCallback(
    (suggestion, currentTranscript, onUpdate) => {
      const updatedTranscript = currentTranscript.replace(suggestion.original, suggestion.suggestion)
      onUpdate(updatedTranscript)
      setCorrectedTranscript(updatedTranscript)
      setGrammarSuggestions((prev) => prev.filter((s) => s !== suggestion))
      if (grammarSuggestions.length <= 1) {
        setShowGrammarPanel(false)
      }
    },
    [grammarSuggestions],
  )

  const applyAllCorrections = useCallback(
    (onUpdate) => {
      if (correctedTranscript) {
        onUpdate(correctedTranscript)
        setGrammarSuggestions([])
        setShowGrammarPanel(false)
      }
    },
    [correctedTranscript],
  )

  const clearGrammarCheck = useCallback(() => {
    setCorrectedTranscript("")
    setGrammarSuggestions([])
    setShowGrammarPanel(false)
    setIsGrammarChecking(false)
  }, [])

  return {
    correctedTranscript,
    isGrammarChecking,
    grammarSuggestions,
    showGrammarPanel,
    checkGrammarWithDelay,
    applySuggestion,
    applyAllCorrections,
    clearGrammarCheck,
    setCorrectedTranscript,
  }
}
